import type { AudioEngine } from './AudioEngine.js';

/**
 * One-shot "whoosh" for the teleport zoom-out / zoom-in cinematic — the rush of being yanked
 * up out of the world and dropped back into a new place. Designed to span the *entire* camera
 * move (the caller passes the move's wall-clock duration), so the sound tracks the motion
 * rather than firing a short blip at the start.
 *
 * Two layers, both shaped to peak at the MIDDLE of the move (the camera uses ease-in-out, so
 * it's travelling fastest there):
 *   - an air-rush noise band whose brightness opens up mid-move then closes again, and
 *   - a tonal pitch glide (up for 'out', down for 'in') for a sense of velocity / direction.
 * A stereo sweep adds the "rushing past you" feel.
 *
 * Routed volumeGain → muteGain → master so the Settings → Sounds slider and per-layer mute
 * scale/silence it. Distinct from the wind "whoosh" layer (`WindSound`, the drive-past wind).
 */
export class TeleportWhoosh {
  private volumeGain: GainNode;
  private muteGain: GainNode;
  private volume = 1.0;
  private muted = false;

  constructor(private audio: AudioEngine, private noiseBuf: AudioBuffer) {
    this.volumeGain = audio.ctx.createGain();
    this.volumeGain.gain.value = this.volume;
    this.muteGain = audio.ctx.createGain();
    this.muteGain.gain.value = 1.0;
    this.volumeGain.connect(this.muteGain).connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.audio.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.audio.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /**
   * @param dir     'out' = flying up/away (zoom out), 'in' = diving into the new place (zoom in).
   * @param seconds wall-clock length of the camera move; the whoosh spans this exactly.
   */
  play(dir: 'out' | 'in', seconds = 1.4): void {
    const ctx = this.audio.ctx;
    if (ctx.state !== 'running') return; // not yet resumed by a user gesture
    const now = ctx.currentTime;
    const dur = Math.max(0.3, seconds);
    const mid = now + dur * 0.5; // camera is fastest here (ease-in-out)
    const end = now + dur;
    // Quiet lead-in / tail so the noise loop doesn't click in or cut off abruptly.
    const a = now + dur * 0.06;
    const r = now + dur * 0.92;

    // --- Air-rush layer: looping noise through a bandpass whose brightness tracks speed. ---
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const offset = Math.random() * Math.max(0, this.noiseBuf.duration - 0.1);

    const lo = 280;
    const hi = 2600;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 0.55;
    // Opens toward `hi` at mid-move (fast) then closes back to `lo` at the ends (slow).
    bp.frequency.setValueAtTime(lo, now);
    bp.frequency.linearRampToValueAtTime(hi, mid);
    bp.frequency.linearRampToValueAtTime(lo, end);

    const noiseGain = ctx.createGain();
    const noisePeak = 0.5;
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(noisePeak, mid);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, end);

    // --- Tonal glide layer: a filtered saw sliding in pitch for velocity + direction. ---
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    const loP = 110;
    const hiP = 560;
    osc.frequency.setValueAtTime(dir === 'out' ? loP : hiP, now);
    osc.frequency.exponentialRampToValueAtTime(dir === 'out' ? hiP : loP, end);
    const oscLp = ctx.createBiquadFilter(); // tame the saw's harshness
    oscLp.type = 'lowpass';
    oscLp.frequency.value = 1400;
    const oscGain = ctx.createGain();
    const oscPeak = 0.12;
    oscGain.gain.setValueAtTime(0.0001, now);
    oscGain.gain.exponentialRampToValueAtTime(oscPeak, mid);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, end);

    // --- Stereo sweep for the "rushing past you" feel (opposite directions out vs in). ---
    const pan = ctx.createStereoPanner();
    pan.pan.setValueAtTime(dir === 'out' ? -0.5 : 0.5, a);
    pan.pan.linearRampToValueAtTime(dir === 'out' ? 0.5 : -0.5, r);

    src.connect(bp).connect(noiseGain).connect(pan).connect(this.volumeGain);
    osc.connect(oscLp).connect(oscGain).connect(pan);

    src.start(now, offset);
    src.stop(end + 0.05);
    osc.start(now);
    osc.stop(end + 0.05);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
